import React, { useState } from "react";
import { TravelData } from "../../components/Data/TravelData";
import {
  Navbar,
  Nav,
  NavDropdown,
  Container,
  Form,
  FormControl,
  Row,
  Col,
  Button,
  Spinner,
} from "react-bootstrap";
import { Link, NavLink } from "react-router-dom";
import { LoadScript } from "@react-google-maps/api";

import { Helmet } from "react-helmet";
import AppNavbar from "../../components/common/AppNavbar";

const GOOGLE_API_KEY = "AIzaSyCpAApH1S5ZOOJsKcdGBV41KvRj2v3xndc"; // <-- Replace this
const LIBRARIES = ["places"];

const Travel = () => {
  // Existing states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [expandedRegions, setExpandedRegions] = useState({});

  // Search related states
  const [searchInput, setSearchInput] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [isSearchCountry, setIsSearchCountry] = useState(null);
  const [searchCityName, setSearchCityName] = useState("");
  const [searchCountryPlaces, setSearchCountryPlaces] = useState([]);
  const [searchPlaces, setSearchPlaces] = useState([]);
  const [searchShrines, setSearchShrines] = useState([]);
  const [searchRestaurants, setSearchRestaurants] = useState([]);

  // Handle continent-country selection
  const handleCountryClick = (continentName, countryName) => {
    setSelectedCountry({ continentName, countryName });
    setExpandedRegions({}); // reset expanded regions on country change
    // Clear search results on manual country select
    clearSearchResults();
  };

  const toggleRegion = (regionName) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionName]: !prev[regionName],
    }));
  };

  const clearSearchResults = () => {
    setSearchInput("");
    setIsSearchCountry(null);
    setSearchCityName("");
    setSearchCountryPlaces([]);
    setSearchPlaces([]);
    setSearchShrines([]);
    setSearchRestaurants([]);
  };

  // Filter continents by selected country
  const filteredContinents = selectedCountry
    ? TravelData.filter((c) => c.continent === selectedCountry.continentName)
    : TravelData;

  // Helpers to check if geocode result is a country
  const checkIfCountry = (placeResults) => {
    if (!placeResults || placeResults.length === 0) return false;
    return placeResults[0].types.includes("country");
  };

  // Fetch tourist places in a country using Places API
  const fetchTouristPlacesInCountry = (countryName, map, service) => {
    setLoadingSearch(true);
    const request = {
      location: map.getCenter(),
      radius: 50000,
      keyword: "tourist attractions",
      type: "tourist_attraction",
    };
    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSearchCountryPlaces(results.slice(0, 10));
      } else {
        setSearchCountryPlaces([]);
      }
      setLoadingSearch(false);
    });
  };

  // Fetch detailed places by keywords for a city/place
  const fetchDetailData = (placeName, map, center) => {
    setLoadingSearch(true);
    const service = new window.google.maps.places.PlacesService(map);

    const search = (keyword, setter) => {
      const request = { location: center, radius: 20000, keyword };
      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const filtered = results.filter(
            (item) => item.photos && item.photos.length > 0
          );
          setter(filtered.slice(0, 8));
        } else {
          setter([]);
        }
        setLoadingSearch(false);
      });
    };

    search(`tourist places in ${placeName}`, setSearchPlaces);
    search(`temples in ${placeName}`, setSearchShrines);
    search(`fine dining in ${placeName}`, setSearchRestaurants);
  };

  // Handle search submit
  const handleSearch = () => {
    if (!searchInput.trim()) return;

    setLoadingSearch(true);
    setSearchCityName("");
    setIsSearchCountry(null);
    setSearchCountryPlaces([]);
    setSearchPlaces([]);
    setSearchShrines([]);
    setSearchRestaurants([]);

    const map = new window.google.maps.Map(document.createElement("div"));
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: searchInput.trim() }, (results, status) => {
      if (status === "OK" && results.length > 0) {
        const location = results[0].geometry.location;
        map.setCenter(location);

        const isCountryResult = checkIfCountry(results);
        setIsSearchCountry(isCountryResult);

        if (isCountryResult) {
          const service = new window.google.maps.places.PlacesService(map);
          fetchTouristPlacesInCountry(searchInput.trim(), map, service);
          setLoadingSearch(false);
        } else {
          setSearchCityName(searchInput.trim());
          fetchDetailData(searchInput.trim(), map, location);
        }
      } else {
        alert("Location not found. Please enter a valid country or place.");
        setLoadingSearch(false);
      }
    });
  };

  return (
    <>
      <Helmet>
        <title>
          World Travel | Best Destinations, Tourism, Trips & Travel Guide –
          BharatAbroad
        </title>
        <meta
          name="title"
          content="World Travel | Best Destinations, Tourism, Trips & Travel Guide – BharatAbroad"
        />
        <meta
          name="description"
          content="Explore world travel guides, top destinations, budget travel tips, luxury vacations, hotel recommendations, itinerary ideas, tourism updates, and global travel inspiration on BharatAbroad."
        />
        <meta
          name="keywords"
          content="world travel guide, international travel destinations, global tourism news, best countries to visit, budget travel worldwide, luxury travel destinations, solo travel tips, family vacation ideas, travel abroad guides, visa free travel, international tourism updates, world heritage tourism, adventure travel worldwide, beach holidays worldwide, mountain destinations world, cultural tourism global, travel tips for NRIs, travel guides for Indians abroad, USA travel news, Canada travel updates, Australia tourism news, Europe travel destinations, Singapore tourism guide, Dubai travel updates, UK travel news, Gulf travel updates, travel advisories worldwide, international airport updates, airline news global, world tourism trends, world airline updates, international visa rules 2026, digital nomad travel, global backpacking destinations, luxury resorts worldwide, international hotel deals, UNESCO world heritage sites, world travel blogs, best destinations to visit 2026, top tourist places worldwide, holiday travel tips, weekend getaway ideas, honeymoon destinations worldwide, family friendly travel destinations, travel itinerary ideas, budget airline deals, travel insurance tips, passport and visa tips for Indians, Indian travellers abroad, NRI travel tips, best time to visit destinations, travel photography tips, eco tourism worldwide, sustainable travel tips, BharatAbroad travel, BharatAbroad tourism, BharatAbroad destinations, BharatAbroad travel guide"
        />

        {/* Technical */}
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta charSet="UTF-8" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Global Travel & Tourism News | Destinations, Trips & Guides – BharatAbroad"
        />
        <meta
          property="og:description"
          content="Explore global travel destinations, tourism trends, holiday ideas, travel guides, hotel stays, adventure trips, and worldwide travel experiences on BharatAbroad."
        />
        <meta property="og:site_name" content="BharatAbroad" />
        <meta property="og:url" content="https://www.bharatabroad.com/travel" />
        <meta
          property="og:image"
          content="https://ayg.s3.us-east-2.amazonaws.com/bharatabroad.com-travel.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Explore the World | Travel Guides, Tourism & Vacation Ideas – BharatAbroad"
        />
        <meta
          name="twitter:description"
          content="Get worldwide travel inspiration with destination guides, tourism updates, hotel recommendations, adventure trips, budget travel tips, and global vacation ideas on BharatAbroad."
        />
        <meta
          name="twitter:image"
          content="https://ayg.s3.us-east-2.amazonaws.com/bharatabroad.com-travel.png"
        />
        <meta name="twitter:site" content="@BharatAbroad" />

        {/* Canonical */}
        <link rel="canonical" href="https://www.bharatabroad.com/travel" />
      </Helmet>
      <AppNavbar />
      <LoadScript
        googleMapsApiKey={GOOGLE_API_KEY}
        libraries={LIBRARIES}
        language="en"
      >
        <div>
          {/* Top Navbar */}
          <Navbar expand="lg" bg="light" className="mb-4 border border-top">
            <Container fluid>
              <Navbar.Brand
                as={Link}
                to="/travel"
                className="text-primary fw-bold"
              >
                Travel Explorer
              </Navbar.Brand>
              <Navbar.Toggle aria-controls="travel-navbar" />

              <Navbar.Collapse id="travel-navbar">
                <Nav className="me-auto">
                  {TravelData.map((continent, ci) => (
                    <NavDropdown
                      title={continent.continent}
                      id={`continent-${ci}`}
                      key={ci}
                    >
                      {continent.countries.map((country, cIndex) => (
                        <NavDropdown.Item
                          key={cIndex}
                          onClick={() =>
                            handleCountryClick(
                              continent.continent,
                              country.name
                            )
                          }
                        >
                          {country.name}
                        </NavDropdown.Item>
                      ))}
                    </NavDropdown>
                  ))}
                </Nav>

                {/* Desktop search form: visible only on lg and above */}
                <Form
                  className="d-none d-lg-flex ms-auto"
                  style={{ maxWidth: "300px" }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch();
                  }}
                >
                  <FormControl
                    type="search"
                    placeholder="Search destinations..."
                    className="me-2"
                    aria-label="Search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    disabled={loadingSearch}
                  >
                    Search
                  </Button>
                </Form>
              </Navbar.Collapse>

              {/* Mobile search form: visible only on small screens, outside collapse */}
              <Form
                className="d-flex d-lg-none mt-2 w-100"
                style={{ maxWidth: "100%" }}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearch();
                }}
              >
                <FormControl
                  type="search"
                  placeholder="Search destinations..."
                  className="me-2"
                  aria-label="Search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  disabled={loadingSearch}
                >
                  Search
                </Button>
              </Form>
            </Container>
          </Navbar>

          {/* Header Section */}
          <Container>
            {/* Show existing filtered continents & countries if no search results */}
            {!searchCityName && !isSearchCountry && (
              <>
                <h4
                  style={{
                    fontSize: "4rem",
                    fontWeight: 700,
                    lineHeight: "0.9",
                    color: "#3a3e61",
                    letterSpacing: "-2px",
                    overflow: "hidden",
                  }}
                  className="text-center"
                >
                  Destination {selectedCountry?.countryName || "Bharat"}
                </h4>
                <h4 className="mb-4 text-center">
                  A potpourri of vibrant cultures, exotic destinations and
                  enduring memories; welcome to one of the heartlands of human
                  civilization.
                </h4>
                {filteredContinents.map((continent, ci) => (
                  <div key={ci}>
                    {continent.countries
                      .filter((country) =>
                        selectedCountry
                          ? country.name === selectedCountry.countryName
                          : country.name === "Bharat"
                      )
                      .map((country, cIndex) => (
                        <div key={cIndex}>
                          {/* Bharat has regions as object */}
                          {country.name === "Bharat" ? (
                            Object.entries(country.regions).map(
                              ([regionName, places]) => {
                                const isExpanded = expandedRegions[regionName];
                                const visiblePlaces = isExpanded
                                  ? places
                                  : places.slice(0, 8);
                                return (
                                  <div key={regionName} className="mb-5">
                                    <h3 className="mb-3 fw-bold">
                                      {regionName}
                                    </h3>
                                    <Row>
                                      {visiblePlaces.map((place, index) => (
                                        <Col
                                          key={index}
                                          xs={12}
                                          sm={6}
                                          md={4}
                                          lg={3}
                                        >
                                          <NavLink
                                            to={`/city/${place.name}`}
                                            style={{
                                              textDecoration: "none",
                                              color: "inherit",
                                            }}
                                          >
                                            <div className="yacht-tile">
                                              <img
                                                src={place.image}
                                                alt={place.name}
                                                className="img-fluid rounded"
                                              />
                                              <div className="overlay text-center mt-2">
                                                <h5 className="fw-bold">
                                                  {place.name}
                                                </h5>
                                              </div>
                                            </div>
                                            <p className="text-muted small">
                                              {place.famousFor}
                                            </p>
                                          </NavLink>
                                        </Col>
                                      ))}
                                    </Row>
                                    {places.length > 8 && (
                                      <div className="text-end mt-3">
                                        <Button
                                          variant="dark"
                                          className="explore-more-btn btn-sm"
                                          onClick={() =>
                                            toggleRegion(regionName)
                                          }
                                        >
                                          {isExpanded
                                            ? "Show Less"
                                            : "Explore More"}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            )
                          ) : country.regions?.Cities?.length ? (
                            (() => {
                              const regionName = "Cities";
                              const places = country.regions.Cities;
                              const isExpanded = expandedRegions[regionName];
                              const visiblePlaces = isExpanded
                                ? places
                                : places.slice(0, 8);
                              return (
                                <div key={regionName} className="mb-5">
                                  <h3 className="mb-3 fw-bold">{regionName}</h3>
                                  <Row>
                                    {visiblePlaces.map((place, index) => (
                                      <Col
                                        key={index}
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={3}
                                      >
                                        <NavLink
                                          to={`/city/${place.name}`}
                                          style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                          }}
                                        >
                                          <div className="yacht-tile">
                                            <img
                                              src={place.image}
                                              alt={place.name}
                                              className="img-fluid rounded"
                                            />
                                            <div className="overlay text-center mt-2">
                                              <h5 className="fw-bold">
                                                {place.name}
                                              </h5>
                                            </div>
                                          </div>
                                          <p className="text-muted small">
                                            {place.famousFor}
                                          </p>
                                        </NavLink>
                                      </Col>
                                    ))}
                                  </Row>
                                  {places.length > 8 && (
                                    <div className="text-end mt-3">
                                      <Button
                                        variant="dark"
                                        className="explore-more-btn btn-sm"
                                        onClick={() => toggleRegion(regionName)}
                                      >
                                        {isExpanded
                                          ? "Show Less"
                                          : "Explore More"}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            <p className="text-muted">
                              No destinations available for this country
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                ))}
              </>
            )}

            {/* SEARCH RESULTS SECTION */}
            {loadingSearch && (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p>Loading places...</p>
              </div>
            )}

            {isSearchCountry && searchCountryPlaces.length > 0 && (
              <Container className="mb-5">
                <h3 className="mb-3 fw-bold">
                  Top Tourist Places in {searchInput.trim()}
                </h3>
                <Row>
                  {searchCountryPlaces.map((place, index) => (
                    <Col
                      key={index}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      className="mb-4"
                    >
                      <NavLink
                        to={`/city/${place.name}`}
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <div className="yacht-tile">
                          <img
                            src={place.image}
                            alt={place.name}
                            className="img-fluid rounded"
                          />
                          <div className="overlay text-center mt-2">
                            <h5 className="fw-bold">{place.name}</h5>
                          </div>
                        </div>
                        <p className="text-muted small">{place.famousFor}</p>
                      </NavLink>
                    </Col>
                  ))}
                </Row>
              </Container>
            )}

            {!isSearchCountry && searchCityName && (
              <Container className="mb-5">
                <h4 className="fw-bold mb-3">
                  Discover {searchCityName}'s Local Attractions, Holy Shrines,
                  and Fine Dining Restaurants
                </h4>

                {/* Local Attractions */}
                {searchPlaces.length > 0 && (
                  <>
                    <h5>Local Attractions</h5>
                    <Row>
                      {searchPlaces.map((item, idx) => (
                        <Col
                          key={idx}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          className="mb-4"
                        >
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              item.name
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div className="yacht-tile">
                              <img
                                src={item.photos?.[0]?.getUrl() || ""}
                                alt={item.name}
                                className="img-fluid rounded"
                              />
                              <div className="overlay text-center mt-2">
                                <h5 className="fw-bold">{item.name}</h5>
                              </div>
                            </div>
                          </a>
                          <p className="text-muted small">
                            {item.vicinity || item.formatted_address}
                          </p>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

                {/* Holy Shrines */}
                {searchShrines.length > 0 && (
                  <>
                    <h5>Holy Shrines</h5>
                    <Row>
                      {searchShrines.map((item, idx) => (
                        <Col
                          key={idx}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          className="mb-4"
                        >
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              item.name
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div className="yacht-tile">
                              <img
                                src={item.photos?.[0]?.getUrl() || ""}
                                alt={item.name}
                                className="img-fluid rounded"
                              />
                              <div className="overlay text-center mt-2">
                                <h5 className="fw-bold">{item.name}</h5>
                              </div>
                            </div>
                          </a>
                          <p className="text-muted small">
                            {item.vicinity || item.formatted_address}
                          </p>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

                {/* Fine Dining */}
                {searchRestaurants.length > 0 && (
                  <>
                    <h5>Fine Dining Restaurants</h5>
                    <Row>
                      {searchRestaurants.map((item, idx) => (
                        <Col
                          key={idx}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          className="mb-4"
                        >
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              item.name
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div className="yacht-tile">
                              <img
                                src={item.photos?.[0]?.getUrl() || ""}
                                alt={item.name}
                                className="img-fluid rounded"
                              />
                              <div className="overlay text-center mt-2">
                                <h5 className="fw-bold">{item.name}</h5>
                              </div>
                            </div>
                          </a>
                          <p className="text-muted small">
                            {item.vicinity || item.formatted_address}
                          </p>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}
              </Container>
            )}
          </Container>
        </div>
      </LoadScript>
    </>
  );
};

export default Travel;
